import React, { useEffect, useState, useRef } from "react"
import {
  SearchspringRecommendProvider,
  SearchspringSuggestProvider,
  useSearchspringSearchContext,
  SearchspringTrendingProvider,
} from "@app/scripts/providers/searchspring"
import { SearchDrawerMenu } from "@app/scripts/components/search-drawer-menu"
import { SearchDrawerResults } from "@app/scripts/components/search-drawer-results"
import { SearchNoResults } from "@app/scripts/components/search-no-results"
import { NoResultsItem } from "@app/types"

export type Props = {
  noResultsData?: NoResultsItem[]
}

export function InstantSearch() {
  const [searchTerm, setSearchTerm] = useState("")
  // Debounced search term added for the suggestions query.
  // This stops it querying the first change which would be the first typed letter or us making multiple requests.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")

  const { response, refine } = useSearchspringSearchContext()

  const results = response?.results || []

  const responseRef = useRef(response)

  useEffect(() => {
    responseRef.current = response
  }, [response])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm === debouncedSearchTerm) return
      setDebouncedSearchTerm(searchTerm)
    }, 700)

    return () => clearTimeout(handler)
  }, [searchTerm])

  useEffect(() => {
    if (debouncedSearchTerm === "") return
    refine("query", debouncedSearchTerm)
  }, [debouncedSearchTerm])

  useEffect(() => {
    const clearBtn = document.querySelector(".search-clear--btn")
    if (clearBtn) {
      clearBtn.classList.toggle("is-hidden", searchTerm === "")
    }
  }, [searchTerm])

  // Handles shopify liquid drawers input and input clear button
  useEffect(() => {
    const searchInput = document.getElementById(
      "side-panel-search-input"
    ) as HTMLInputElement | null
    const clearBtn = document.querySelector(
      ".search-clear--btn"
    ) as HTMLButtonElement | null

    if (!searchInput) return

    const handleInput = (e: Event) =>
      setSearchTerm((e.target as HTMLInputElement).value ?? "")
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        // Now use responseRef.current to get the latest response
        window.location.href = responseRef.current?.merchandising?.redirect
          ? responseRef.current.merchandising.redirect
          : `/search?query=${searchInput.value}`
      }
    }
    const handleClear = () => {
      searchInput.value = ""
      setSearchTerm("")
    }

    searchInput.addEventListener("input", handleInput)
    searchInput.addEventListener("keydown", handleKeyDown)
    clearBtn?.addEventListener("click", handleClear)

    return () => {
      searchInput.removeEventListener("input", handleInput)
      searchInput.removeEventListener("keydown", handleKeyDown)
      clearBtn?.removeEventListener("click", handleClear)
    }
  }, [])

  const searchDrawerTitle =
    results.length > 0 && searchTerm.length > 0
      ? "Suggestions"
      : "Popular Searches"

  const noResults =
    results.length === 0 && searchTerm !== ""
      ? `No results for "${searchTerm}". Check your spelling, try another search or browse our popular collections below.`
      : ""

  const showNoResults = debouncedSearchTerm !== "" && !results?.length

  // Below items to be reviewed and implemented when search spring data is available - awaiting searchspring response
  return (
    <div className="ss-search-drawer">
      {/* Search drawer menu */}
      <div
        data-ui={!!debouncedSearchTerm && "hidden"}
        className="ss-search-drawer__results"
      >
        <SearchspringTrendingProvider category="products">
          <SearchDrawerMenu
            title={searchDrawerTitle}
            noResults={noResults}
            provider={"trending"}
            showNoResults={showNoResults}
          />
        </SearchspringTrendingProvider>
        <SearchspringRecommendProvider profile="popular-searches" limit={4}>
          <SearchDrawerResults recommendations={true} />
        </SearchspringRecommendProvider>
      </div>
      <div
        data-ui={!debouncedSearchTerm && !results.length && "hidden"}
        className="ss-search-drawer__results"
      >
        <SearchspringSuggestProvider
          key={debouncedSearchTerm}
          category="products"
          query={debouncedSearchTerm}
        >
          <SearchDrawerMenu
            showNoResults={showNoResults}
            title={searchDrawerTitle}
            noResults={noResults}
            provider={"suggest"}
          />
        </SearchspringSuggestProvider>
      </div>
      <div
        data-ui={!debouncedSearchTerm && "hidden"}
        className="ss-search-drawer__results"
      >
        {!results.length && <SearchNoResults />}
      </div>
      <SearchDrawerResults resultsData={results} />

      {/* Search drawer view results btn - in this file */}
      {results.length > 0 && searchTerm.length > 0 && (
        <div className="search-drawer--footer">
          <a
            href={
              response?.merchandising?.redirect
                ? response?.merchandising?.redirect
                : `/search?query=${searchTerm}`
            }
            className="search-drawer--footer-results-btn button large"
          >
            View {response?.pagination?.totalResults} Results
          </a>
        </div>
      )}
    </div>
  )
}
